import Color from '@/Enums/Color.enum';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import { Box, Typography } from '@mui/material';
import { ReactNode, memo, useCallback } from 'react';
import RemoveButton from './RemoveButton';

enum BoundaryPosition {
  Start = 'start',
  End = 'end',
}

interface IGroupBoundaryProps {
  includeBoundary: boolean;
  position: BoundaryPosition;
}

const BoundaryNode: Record<BoundaryPosition, ReactNode> = {
  [BoundaryPosition.Start]: '(',
  [BoundaryPosition.End]: ')',
};

const GroupBoundary = memo(({ includeBoundary, position }: IGroupBoundaryProps) => {
  if (!includeBoundary) return null;
  return <Box sx={{ color: Color.Primary, fontSize: '20px', fontWeight: 700 }}>{BoundaryNode[position]}</Box>;
});

const OrConnector = memo(() => (
  <Typography component='p' sx={{ color: Color.Primary, fontSize: '12px', lineHeight: '16px', fontWeight: 700 }}>
    OR
  </Typography>
));

interface IProps<V> {
  includeBoundaries: boolean;
  audience: IAudienceControlOption<V>;
  onRemove: (audienceId: IAudienceControlOption<V>['value']) => void;
  first: boolean;
  last: boolean;
}

const OrGroupItem = <V extends string | number>({ includeBoundaries, audience, first, last, onRemove }: IProps<V>) => {
  const onClick = useCallback(() => onRemove(audience.value), [audience, onRemove]);
  return (
    <>
      {first && <GroupBoundary includeBoundary={includeBoundaries} position={BoundaryPosition.Start} />}
      <div className='px-3 py-2 bg-[#1b43321f] rounded-full flex gap-1'>
        <Typography component='p' sx={{ color: Color.Primary, fontSize: '12px', lineHeight: '16px', fontWeight: 700 }}>
          {audience.label}
        </Typography>
        <RemoveButton onClick={onClick} />
      </div>
      {!last && <OrConnector />}
      {last && <GroupBoundary includeBoundary={includeBoundaries} position={BoundaryPosition.End} />}
    </>
  );
};

export default OrGroupItem;
